import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  Button,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  makeStyles,
  Box,
  CircularProgress,
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { connect } from 'react-redux'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import { Translate } from 'react-localize-redux'
import { fetchOrganizationMembers } from './api'

const useStyles = makeStyles((theme) => ({
  noPaddingOption: {
    pointerEvents: 'none',
    '&:hover': {
      pointerEvents: 'none',
    },
    padding: '4px',
    backgroundColor: 'transparent !important',
    '&[data-focus="true"]': {
      backgroundColor: 'transparent !important',
    },
  },
  autocompleteInputRoot: {
    paddingRight: '15px!important',
  },
  autocompleteListbox: {
    maxHeight: '30vh',
    padding: '6px 4px 0px 4px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  inputLabel: {
    textAlign: 'center',
    top: '50%',
    transform: 'translateY(-50%)',
    transition: 'all 0.2s ease-out',
    paddingLeft: '12px',
  },
  inputLabelShrink: {
    top: 0,
    transform: 'translateY(0)',
    textAlign: 'left',
    paddingLeft: '0px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    background: 'white',
    padding: '4px 10px',
    borderRadius: '8px',
    cursor: 'default',
    transition: 'none',
  },
  optionText: {
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  optionSubText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  addedText: {
    marginRight: '10px',
    fontSize: '12px',
    fontWeight: 600,
  },
  addButton: {
    borderRadius: 999,
    padding: '4px 14px',
    fontWeight: 600,
    pointerEvents: 'auto',
    textTransform: 'none',
    fontSize: '12px',
  },
  noOptionsPaper: {
    borderRadius: '8px',
    background: '#f2f2f2',
  },
  noOptionsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80px',
    flexDirection: 'column',
    backgroundColor: 'white',
    padding: '2px',
    borderRadius: '6px',
  },
  noOptionsText: {
    marginBottom: '10px',
  },
  noOptionsButton: {
    borderRadius: '16px',
  },
  textBox: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  buttonBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap',
    paddingLeft: theme.spacing(1),
  },
  noOptions: {
    display: 'none',
  },
  loadMore: {
    width: '100%',
    height: 48,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const LOAD_MORE_OPTION = { isLoadMore: true }
const LOADING_OPTION = { isLoading: true, first_name: 'Loading...' }

const AddNewPlayer = ({
  team,
  currentClub,
  handleAddClick,
  onSearchValue,
  setSearchOpen,
  searchOpen,
  searchValue,
  setSearchValue,
}) => {
  const [filteredMembers, setFilteredMembers] = useState([])
  const [page, setPage] = useState(0)
  const [hasMorePages, setHasMorePages] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false)

  const searchValueRef = useRef('')
  const listboxRef = useRef(null)

  const classes = useStyles()

  const fetchFilteredMembers = (search = '', requestedPage = 1) => {
    setIsLoading(true)
    fetchOrganizationMembers(team.organization_uuid, {
      'filter[club_uuid]': currentClub.uuid,
      'filter[search]': search,
      'association_check_context[entity]': 'team',
      'association_check_context[uuid]': team.uuid,
      'association_check_context[type]': 'player',
      page: requestedPage,
      per_page: 6,
    })
      .then((res) => {
        const results = res.data || []
        if (requestedPage === 1) {
          setFilteredMembers(results)
        } else {
          setFilteredMembers((prev) => [...prev, ...results])
        }

        const meta = res.meta || {}
        const currentPage = meta.current_page || requestedPage
        const lastPage = meta.last_page || requestedPage
        setHasMorePages(currentPage < lastPage)

        if (search.trim()) {
          setSearchOpen(true)
        }
      })
      .catch((err) => {
        console.error('Error fetching filtered members:', err)
        setSearchOpen(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const debouncedFetchFilteredMembers = useCallback(
    debounce(() => {
      const currentValue = searchValueRef.current.trim()
      if (currentValue.length > 0) {
        setPage(1)
        setHasMorePages(true)
        fetchFilteredMembers(currentValue, 1)
      } else {
        setFilteredMembers([])
        setSearchOpen(false)
      }
    }, 200),
    [team.organization_uuid]
  )

  const handleSearch = (event, value) => {
    searchValueRef.current = value
    setSearchValue(value)

    if (onSearchValue) {
      onSearchValue(value)
    }

    if (value.trim().length === 0) {
      setFilteredMembers([])
      setSearchOpen(false)
    } else {
      debouncedFetchFilteredMembers()
    }
  }

  useEffect(() => {
    if (!searchValue.trim()) {
      setSearchOpen(false)
    }
  }, [searchValue])

  const loadMore = () => {
    const nextPage = page + 1
    setIsLoadMoreLoading(true)

    const previousCount = filteredMembers.length

    fetchOrganizationMembers(team.organization_uuid, {
      'filter[club_uuid]': currentClub.uuid,
      'filter[search]': searchValueRef.current,
      'association_check_context[entity]': 'team',
      'association_check_context[uuid]': team.uuid,
      'association_check_context[type]': 'player',
      page: nextPage,
      per_page: 6,
    })
      .then((res) => {
        const results = res.data || []

        setFilteredMembers((prev) => [...prev, ...results])

        setTimeout(() => {
          const listbox = listboxRef.current

          if (listbox) {
            const optionElements = listbox.querySelectorAll('[role="option"]')

            const newItem = optionElements[previousCount]

            if (newItem) {
              const listboxTop = listbox.getBoundingClientRect().top
              const itemTop = newItem.getBoundingClientRect().top
              const offset = itemTop - listboxTop

              listbox.scrollTop += offset
            }
          }
        }, 0)

        const meta = res.meta || {}
        const currentPage = meta.current_page || nextPage
        const lastPage = meta.last_page || nextPage
        setHasMorePages(currentPage < lastPage)
        setPage(currentPage)
      })
      .catch((err) => {
        console.error('Error loading more members:', err)
      })
      .finally(() => {
        setIsLoadMoreLoading(false)
      })
  }

  useEffect(() => {
    return () => {
      debouncedFetchFilteredMembers.cancel()
    }
  }, [debouncedFetchFilteredMembers])

  const optionsWithLoadMore = useMemo(() => {
    if (isLoading) {
      return [LOADING_OPTION]
    }
    if (hasMorePages) {
      return [...filteredMembers, LOAD_MORE_OPTION]
    }

    return filteredMembers
  }, [filteredMembers, hasMorePages, isLoading])

  return (
    <Paper>
      <Autocomplete
        classes={{
          option: classes.noPaddingOption,
          inputRoot: classes.autocompleteInputRoot,
          listbox: classes.autocompleteListbox,
          noOptions: classes.noOptions,
        }}
        ListboxProps={{
          ref: listboxRef,
        }}
        filterOptions={(options) => options}
        options={optionsWithLoadMore}
        getOptionLabel={(option) =>
          option.isLoadMore
            ? 'LOAD_MORE'
            : `${option.first_name || ''} ${option.last_name || ''}`
        }
        renderOption={(option) => {
          if (option.isLoading) {
            return (
              <Box
                style={{ width: '100%', height: 48 }}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress size={24} />
              </Box>
            )
          }

          if (option.isLoadMore && !isLoading) {
            return (
              <Box display="flex" justifyContent="center" width="100%">
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    loadMore()
                  }}
                  className={classes.addButton}
                >
                  <Translate id="videos.more" />
                </Button>
              </Box>
            )
          }

          return (
            <Box className={classes.row}>
              <Box className={classes.textBox}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {option.first_name} {option.last_name}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className={classes.optionSubText}
                >
                  {option.email || <>&nbsp;</>}
                </Typography>
              </Box>
              <Box className={classes.buttonBox}>
                {option.already_added ? (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.addedText}
                  >
                    <Translate id="button.added" />
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAddClick(option)
                    }}
                    className={classes.addButton}
                  >
                    <Translate id="button.add" />
                  </Button>
                )}
              </Box>
            </Box>
          )
        }}
        inputValue={searchValue}
        onInputChange={handleSearch}
        open={searchOpen}
        clearOnBlur={false}
        PaperComponent={({ children }) => {
          return (
            <Paper className={classes.noOptionsPaper}>
              {children}

              {isLoadMoreLoading && (
                <div className={classes.loadMore}>
                  <CircularProgress size={24} />
                </div>
              )}
              <Box display="flex" justifyContent="flex-start" padding="12px">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddClick(null)
                  }}
                  className={classes.addButton}
                >
                  <Translate id="team.members.add-new-player" />
                </Button>
              </Box>
            </Paper>
          )
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={<Translate id="team.members.search-by-name" />}
            variant="outlined"
            fullWidth
            InputLabelProps={{
              classes: {
                root: classes.inputLabel,
                shrink: classes.inputLabelShrink,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position="end">
                  {searchValue.trim() ? (
                    <ClearIcon
                      className={classes.clearIcon}
                      onClick={() => {
                        setSearchValue('')
                        setFilteredMembers([])
                        setSearchOpen(false)
                      }}
                    />
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Paper>
  )
}

AddNewPlayer.defaultProps = {
  onClose: null,
}

AddNewPlayer.propTypes = {
  team: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    organization_uuid: PropTypes.string.isRequired,
  }).isRequired,
  currentClub: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func,
  handleAddClick: PropTypes.func.isRequired,
  onSearchValue: PropTypes.func.isRequired,
  setSearchOpen: PropTypes.func.isRequired,
  searchOpen: PropTypes.bool.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
}

export default connect(({ team, clubs: { current } }) => ({
  team,
  currentClub: current,
}))(AddNewPlayer)
